import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { ColorsConstants, grayFontColor } from "../../../../cssUtils/constants";
import typography from "../../../../cssUtils/typography";

export const SectionTitle = ({ children }) => (
  <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mt: 1 }}>
    {children}
  </Typography>
);

// Fields that belong to the section title above them, indented like the mockup
export const SubSection = ({ children }) => (
  <Stack spacing={2} sx={{ pl: 1.5 }}>
    {children}
  </Stack>
);

// Title row of an OTP node panel: coloured square, name, and the soft "When to Use" card
export const OtpPanelHeader = ({ title, color, whenToUse }) => (
  <Stack spacing={2}>
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box sx={{ width: 10, height: 10, borderRadius: "2px", backgroundColor: color }} />
      <Typography sx={{ ...typography.bodyLarge, fontSize: 15, fontWeight: 600 }}>
        {title}
      </Typography>
    </Stack>
    {whenToUse && (
      <Box
        sx={{
          p: 1.75,
          borderRadius: 2,
          border: "1px solid",
          borderColor: ColorsConstants.primaryGray10,
          backgroundColor: "#f5f6f8",
        }}
      >
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mb: 0.75 }}>
          When to Use:
        </Typography>
        <Typography sx={{ ...typography.bodyLarge, lineHeight: 1.55 }}>{whenToUse}</Typography>
      </Box>
    )}
  </Stack>
);

// "POST /api/v2/otp/verify/" line with the method chip
export const ApiLine = ({ method = "POST", path }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1}
    sx={{
      px: 1.25,
      py: 1,
      borderRadius: 1.5,
      border: "1px solid",
      borderColor: ColorsConstants.primaryGray10,
      backgroundColor: "#f5f6f8",
    }}
  >
    <Box
      sx={{
        px: 0.9,
        py: 0.25,
        borderRadius: 1,
        color: "#fff",
        fontSize: 11.5,
        fontWeight: 600,
        backgroundColor: "#2e7d32",
      }}
    >
      {method}
    </Box>
    <Typography sx={{ fontFamily: "monospace", fontSize: 12.5, wordBreak: "break-all" }}>
      {path}
    </Typography>
  </Stack>
);

// `otp_ref_id → [Store As field]`, the field name on the left and the editable part on the right
export const MappingRow = ({ name, arrow = "←", children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 18px minmax(0, 1fr)",
      gap: 1,
      alignItems: "center",
    }}
  >
    <Typography
      sx={{
        px: 1.25,
        py: 1.4,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontFamily: "monospace",
        fontSize: 12.5,
        borderRadius: 1,
        border: "1px solid",
        borderColor: ColorsConstants.primaryGray10,
        backgroundColor: "#f5f6f8",
      }}
    >
      {name}
    </Typography>
    <Typography sx={{ textAlign: "center", color: grayFontColor }}>{arrow}</Typography>
    {children}
  </Box>
);

export const HelperText = ({ children }) => (
  <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
    {children}
  </Typography>
);

// A value that comes from the tenant, the flow or the OTP service and can't be edited here
export const ReadOnlyField = ({ label, value, helperText, type = "text" }) => (
  <TextField
    fullWidth
    size="small"
    disabled
    label={label}
    type={type}
    value={value ?? ""}
    helperText={helperText}
  />
);

export const JsonBox = ({ children }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      p: 1.5,
      overflowX: "auto",
      fontSize: 12,
      lineHeight: 1.5,
      borderRadius: 1.5,
      border: "1px solid",
      borderColor: ColorsConstants.primaryGray10,
      backgroundColor: ColorsConstants.primaryBlue60,
    }}
  >
    {children}
  </Box>
);

// rows: [{ response: "invalid_code", detail: "Wrong code", route: "Ask again" }]
const STATUS_PILLS = {
  2: { color: "#1b5e20", backgroundColor: "#e3f5ec" },
  4: { color: "#8a2a12", backgroundColor: "#fdecea" },
  5: { color: "#7a4a00", backgroundColor: "#fff4de" },
};

// "400 invalid_code" -> pill "400" + detail; rows without a status code render as plain text
const splitResponse = (response) => {
  const match = /^(\d{3}(?:\s*\/\s*\d{3})*)\s*(.*)$/.exec(response);
  return match ? { status: match[1], name: match[2] } : { status: null, name: response };
};

export const OutputsTable = ({ rows }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell sx={{ pl: 0 }}>Response</TableCell>
        <TableCell sx={{ pr: 0 }}>Route to</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map((row) => (
        <TableRow key={`${row.response}-${row.route}`}>
          <TableCell sx={{ pl: 0 }}>
            <Stack>
              <Typography component="div" sx={{ ...typography.bodySmall, fontWeight: 600 }}>
                {(() => {
                  const { status, name } = splitResponse(row.response);
                  return (
                    <>
                      {status && (
                        <Box
                          component="span"
                          sx={{
                            mr: 0.75,
                            px: 0.75,
                            borderRadius: 1,
                            ...STATUS_PILLS[status[0]],
                          }}
                        >
                          {status}
                        </Box>
                      )}
                      {name}
                    </>
                  );
                })()}
              </Typography>
              <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
                {row.detail}
              </Typography>
            </Stack>
          </TableCell>
          <TableCell sx={{ pr: 0, whiteSpace: "nowrap" }}>{row.route}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

export const END_FAILED = "End · Not verified";
export const END_VERIFIED = "End · Verified";
